import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "../../UIElements/Icon";
import { OverlayPortalContext } from "../overlay-portal-context";
import {
  isPortalledOverlayTarget,
  isTopOverlayRoot,
  registerOverlayRoot,
  unregisterOverlayRoot,
} from "../overlay-isolation";
import "../overlay-foundations.css";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  // Also matches iframes that opt in with tabIndex={0} (e.g. the Calendly
  // booking embed). Escape can't be observed inside a cross-origin frame, so
  // frames shown as secondary content (plugin previews) stay out unless opted in.
  "[tabindex]:not([tabindex='-1'])",
].join(",");

// Tab can't be intercepted while focus is inside a cross-origin iframe: these
// sentinels at both ends of the dialog catch focus leaving it and wrap it back.
const FOCUS_GUARD_ATTRIBUTE = "data-ui-modal-focus-guard";

const hasOpenNestedEscapeLayer = (modalRoot) =>
  Boolean(
    modalRoot.querySelector(
      '[data-ui-overlay-dismiss-layer]:not([data-state="closed"])',
    ),
  );

const getFocusableElements = (root) =>
  Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (element) =>
      !element.hasAttribute(FOCUS_GUARD_ATTRIBUTE) &&
      !element.hasAttribute("disabled") &&
      element.getAttribute("aria-hidden") !== "true" &&
      element.getAttribute("aria-disabled") !== "true",
  );

const Modal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  maxWidth = "max-w-2xl",
  showCloseButton = true,
  closeOnBackdrop = true,
  closeOnEscape = true,
  initialFocusRef,
  closeButtonLabel,
  className = "",
}) => {
  const { t } = useTranslation("common");
  const titleId = useId();
  const descriptionId = useId();
  const modalRootRef = useRef(null);
  const [overlayPortalContainer, setOverlayPortalContainer] = useState(null);
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const restoreFocusRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const attachModalRoot = useCallback((node) => {
    modalRootRef.current = node;
    if (node) setOverlayPortalContainer(node);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const modalRoot = modalRootRef.current;
    const dialog = dialogRef.current;
    if (!modalRoot || !dialog) return;

    restoreFocusRef.current = document.activeElement;
    registerOverlayRoot(modalRoot, restoreFocusRef.current);

    const requestedInitialFocus = initialFocusRef?.current;
    const focusTarget =
      (requestedInitialFocus && dialog.contains(requestedInitialFocus)
        ? requestedInitialFocus
        : null) ||
      closeButtonRef.current ||
      getFocusableElements(dialog)[0] ||
      dialog;
    focusTarget.focus({ preventScroll: true });

    const handleEscapeKey = (e) => {
      if (!isTopOverlayRoot(modalRoot)) return;

      // Radix popovers are portalized outside the dialog DOM tree. Let the
      // active, visually topmost overlay own Tab and Escape before the modal.
      if (isPortalledOverlayTarget(e.target, dialog)) return;

      if (e.key === "Escape" && hasOpenNestedEscapeLayer(modalRoot)) return;

      if (e.key === "Escape" && closeOnEscape) {
        e.preventDefault();
        e.stopPropagation();
        onCloseRef.current();
        return;
      }

      if (e.key !== "Tab") return;

      const focusableElements = getFocusableElements(dialog);
      if (focusableElements.length === 0) {
        e.preventDefault();
        dialog.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements.at(-1);
      const activeElement = document.activeElement;

      if (!dialog.contains(activeElement)) {
        e.preventDefault();
        firstElement.focus();
      } else if (activeElement === dialog) {
        // Clicking non-interactive text focuses the dialog container itself;
        // the browser would move to the start guard next, so wrap explicitly.
        e.preventDefault();
        (e.shiftKey ? lastElement : firstElement).focus();
      } else if (e.shiftKey && activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleEscapeKey, true);

    return () => {
      document.removeEventListener("keydown", handleEscapeKey, true);
      const restoreTarget = unregisterOverlayRoot(modalRoot);
      if (restoreTarget?.isConnected) {
        queueMicrotask(() => {
          if (restoreTarget.isConnected) {
            restoreTarget.focus({ preventScroll: true });
          }
        });
      }
    };
  }, [closeOnEscape, initialFocusRef, isOpen]);

  if (!isOpen) return null;

  const wrapFocusFromGuard = (edge, event) => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const focusableElements = getFocusableElements(dialog);
    // The start guard wraps to the last element only when focus moves backwards
    // from inside the dialog (e.g. Shift+Tab out of a cross-origin iframe, which
    // reports no relatedTarget). Focus arriving from the dialog container or from
    // outside is moving forwards, so it goes to the first element.
    const from = event.relatedTarget;
    const arrivesForward =
      from === dialog || (from instanceof Node && !dialog.contains(from));
    const target =
      (edge === "start" && !arrivesForward
        ? focusableElements.at(-1)
        : focusableElements[0]) || dialog;
    target.focus({ preventScroll: true });
  };

  const focusGuard = (edge) => (
    <span
      {...{ [FOCUS_GUARD_ATTRIBUTE]: edge }}
      tabIndex={0}
      className="sr-only"
      onFocus={(event) => wrapFocusFromGuard(edge, event)}
    />
  );

  const handleBackdropClick = (e) => {
    if (closeOnBackdrop && e.target === e.currentTarget) {
      onClose();
    }
  };

  const modal = (
    <div
      ref={attachModalRoot}
      data-ui-modal-root=""
      className="ui-modal-backdrop fixed inset-0 flex items-center justify-center p-0 sm:p-4"
      style={{ zIndex: "var(--layer-dialog)" }}
      onClick={handleBackdropClick}
    >
      <OverlayPortalContext.Provider value={overlayPortalContainer}>
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          aria-describedby={subtitle ? descriptionId : undefined}
          tabIndex={-1}
          className={`ui-modal-surface ui-overlay-motion h-full max-h-[100dvh] w-full overflow-y-auto overscroll-contain border-0 outline-none sm:h-auto sm:max-h-[min(90dvh,calc(100dvh-2rem))] sm:w-full sm:rounded-lg sm:border ${maxWidth} ${className}`}
        >
          {focusGuard("start")}
          {/* Header */}
          {(title || subtitle || showCloseButton) && (
            <div className="ui-modal-header flex items-start justify-between border-b p-4 sm:p-6">
              <div className="min-w-0 flex-1 pr-2">
                {title && (
                  <h2
                    id={titleId}
                    className="truncate text-lg font-bold text-inherit sm:text-xl"
                  >
                    {title}
                  </h2>
                )}
                {subtitle && (
                  <p
                    id={descriptionId}
                    className="ui-modal-description mt-1 text-sm sm:text-base"
                  >
                    {subtitle}
                  </p>
                )}
              </div>
              {showCloseButton && (
                <button
                  ref={closeButtonRef}
                  type="button"
                  onClick={onClose}
                  aria-label={closeButtonLabel || t("actions.close")}
                  className="ui-modal-description hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-md transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none"
                >
                  <Icon name="X" className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              )}
            </div>
          )}

          {/* Content */}
          <div className="p-4 sm:p-6">{children}</div>
          {focusGuard("end")}
        </div>
      </OverlayPortalContext.Provider>
    </div>
  );

  return createPortal(modal, document.body);
};

export default Modal;
